import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useMotionValue, useSpring, useInView, useScroll, useTransform } from 'framer-motion';
import { Search, ArrowRight, Shield, Scale, Zap, Globe, ChevronDown, Sparkles, Lock, Ban, Eye, AlertTriangle, ShieldOff, MessageSquareOff, Check, Star, ChevronUp, FileText, Clock, Users, Award, Heart } from 'lucide-react';
import { FaMeta, FaGoogle, FaAmazon, FaTiktok, FaSnapchat, FaApple, FaMicrosoft, FaSpotify } from 'react-icons/fa6';
import { FaInstagram, FaYoutube, FaTwitter, FaLinkedin, FaPinterest, FaReddit, FaDiscord, FaTelegram, FaWhatsapp, FaPaypal, FaEbay, FaAirbnb } from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { useTheme } from '../App';
import { suggestionsAPI } from '../lib/api';

const TECH_COMPANIES = [
  { icon: FaMeta, name: 'Meta', color: '#1877F2', size: 'lg' },
  { icon: FaGoogle, name: 'Google', color: '#4285F4', size: 'lg' },
  { icon: FaAmazon, name: 'Amazon', color: '#FF9900', size: 'lg' },
  { icon: FaTiktok, name: 'TikTok', color: '#00F2EA', size: 'md' },
  { icon: FaSnapchat, name: 'Snapchat', color: '#FFFC00', size: 'md' },
  { icon: FaApple, name: 'Apple', color: '#A2AAAD', size: 'lg' },
  { icon: FaMicrosoft, name: 'Microsoft', color: '#00BCF2', size: 'lg' },
  { icon: FaSpotify, name: 'Spotify', color: '#1DB954', size: 'md' },
  { icon: FaInstagram, name: 'Instagram', color: '#E4405F', size: 'md' },
  { icon: FaYoutube, name: 'YouTube', color: '#FF0000', size: 'lg' },
  { icon: FaTwitter, name: 'X/Twitter', color: '#1DA1F2', size: 'md' },
  { icon: FaLinkedin, name: 'LinkedIn', color: '#0A66C2', size: 'sm' },
  { icon: FaPinterest, name: 'Pinterest', color: '#E60023', size: 'sm' },
  { icon: FaReddit, name: 'Reddit', color: '#FF5700', size: 'sm' },
  { icon: FaDiscord, name: 'Discord', color: '#5865F2', size: 'sm' },
  { icon: FaTelegram, name: 'Telegram', color: '#26A5E4', size: 'sm' },
  { icon: FaWhatsapp, name: 'WhatsApp', color: '#25D366', size: 'sm' },
  { icon: FaPaypal, name: 'PayPal', color: '#00457C', size: 'sm' },
  { icon: FaEbay, name: 'eBay', color: '#E53238', size: 'sm' },
  { icon: FaAirbnb, name: 'Airbnb', color: '#FF5A5F', size: 'sm' },
];

const ISSUE_ICONS = [Ban, Lock, Eye, AlertTriangle, ShieldOff, MessageSquareOff];

/* ═══════════════════════════════════════════════════════
   ANIMATED COUNTER - counts up on scroll
   ═══════════════════════════════════════════════════════ */
const AnimatedCounter = ({ value, suffix = '', isDark }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [display, setDisplay] = useState('0');

  useEffect(() => {
    if (!isInView) return;
    const num = parseInt(value.replace(/[^0-9]/g, ''), 10);
    if (isNaN(num)) { setDisplay(value); return; }
    const duration = 2000;
    const steps = 60;
    const increment = num / steps;
    let current = 0;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      current = Math.min(Math.round(increment * step), num);
      setDisplay(current.toLocaleString());
      if (step >= steps) clearInterval(timer);
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isInView, value]);

  return (
    <span ref={ref} className={`text-4xl md:text-6xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
      {display}{suffix}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════
   FLOATING 3D LOGO
   ═══════════════════════════════════════════════════════ */
const Logo3D = ({ Icon, color, index, total, isDark }) => {
  const angle = (index / total) * Math.PI * 2;
  const radius = 35 + (index % 3) * 12;
  const x = 50 + Math.cos(angle) * radius;
  const y = 50 + Math.sin(angle) * radius * 0.5;
  const z = Math.sin(angle + index) * 30;
  const scale = 0.6 + (z + 30) / 60 * 0.6;
  const opacity = isDark ? 0.04 + (z + 30) / 60 * 0.08 : 0.03 + (z + 30) / 60 * 0.06;
  const sizeMap = { lg: 'w-14 h-14 md:w-20 md:h-20', md: 'w-10 h-10 md:w-16 md:h-16', sm: 'w-8 h-8 md:w-12 md:h-12' };

  return (
    <motion.div
      className="absolute pointer-events-none flex flex-col items-center gap-1"
      style={{ left: `${x}%`, top: `${y}%`, zIndex: Math.round(z + 30) }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{
        opacity,
        scale,
        y: [0, -8 - (index % 5) * 3, 0, 6 + (index % 4) * 2, 0],
        x: [0, 4 + (index % 3) * 2, 0, -4 - (index % 2) * 2, 0],
        rotateY: [0, 5, 0, -5, 0],
      }}
      transition={{
        opacity: { duration: 1.5, delay: index * 0.08 },
        scale: { duration: 1.5, delay: index * 0.08 },
        y: { duration: 6 + index * 0.5, repeat: Infinity, ease: 'easeInOut' },
        x: { duration: 7 + index * 0.6, repeat: Infinity, ease: 'easeInOut' },
        rotateY: { duration: 8, repeat: Infinity, ease: 'easeInOut' },
      }}
    >
      <div className="relative">
        <Icon className={`${sizeMap[TECH_COMPANIES[index]?.size || 'md']}`} style={{ color, filter: isDark ? `drop-shadow(0 0 20px ${color}40)` : 'none' }} />
        <div className="absolute inset-0 rounded-full" style={{ background: `radial-gradient(circle, ${color}10 0%, transparent 70%)`, transform: 'scale(2)' }} />
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════
   CLOUD TAG (inside search dropdown)
   ═══════════════════════════════════════════════════════ */
const CloudTag = ({ text, index, onClick, isDark, icon: IconComp }) => (
  <motion.button
    onClick={onClick}
    initial={{ opacity: 0, scale: 0, y: 20 }}
    animate={{ opacity: 1, scale: 1, y: [0, -4 - (index % 3) * 2, 0, 3 + (index % 2) * 2, 0], x: [0, 2 + (index % 3), 0, -2 - (index % 2), 0] }}
    exit={{ opacity: 0, scale: 0.8, y: 10 }}
    transition={{
      opacity: { duration: 0.4, delay: index * 0.05 },
      scale: { duration: 0.5, delay: index * 0.05, type: 'spring', stiffness: 200 },
      y: { duration: 3 + (index % 3), repeat: Infinity, ease: 'easeInOut', delay: index * 0.2 },
      x: { duration: 4 + (index % 2), repeat: Infinity, ease: 'easeInOut', delay: index * 0.3 },
    }}
    whileHover={{ scale: 1.15, y: -4, zIndex: 10 }}
    whileTap={{ scale: 0.95 }}
    className={`group relative px-4 py-2.5 rounded-2xl text-sm cursor-pointer transition-all whitespace-nowrap border backdrop-blur-md ${
      isDark
        ? 'bg-white/[0.04] hover:bg-white/[0.12] border-white/[0.06] hover:border-white/[0.15] text-white/70 hover:text-white'
        : 'bg-blue-50/80 hover:bg-blue-100 border-blue-100/80 hover:border-blue-200 text-blue-700 hover:text-blue-900'
    }`}
  >
    <span className="flex items-center gap-2">
      {IconComp && <IconComp className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />}
      {text}
    </span>
  </motion.button>
);

/* ═══════════════════════════════════════════════════════
   FLOATING CLOUD TAG (outside search)
   ═══════════════════════════════════════════════════════ */
const FloatingCloudTag = ({ text, index, onClick, isDark }) => {
  const delay = index * 0.12;
  const floatDuration = 5 + (index % 4) * 0.8;
  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, y: 30, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1, y: [0, -8 - (index % 4) * 3, 0, 6 + (index % 3) * 2, 0], x: [0, 5 + (index % 3) * 2, 0, -5 - (index % 2) * 2, 0] }}
      transition={{
        opacity: { duration: 0.6, delay },
        scale: { duration: 0.6, delay, type: 'spring' },
        y: { duration: floatDuration, repeat: Infinity, ease: 'easeInOut', delay: delay * 0.5 },
        x: { duration: floatDuration * 1.3, repeat: Infinity, ease: 'easeInOut', delay: delay * 0.7 },
      }}
      whileHover={{ scale: 1.15, y: -6 }}
      whileTap={{ scale: 0.95 }}
      className={`relative px-5 py-2.5 rounded-full text-sm backdrop-blur-md cursor-pointer transition-all whitespace-nowrap border group ${
        isDark
          ? 'bg-white/[0.05] hover:bg-white/[0.15] border-white/[0.08] hover:border-white/[0.2] text-white/60 hover:text-white'
          : 'bg-white/80 hover:bg-blue-50 border-gray-200/80 hover:border-blue-300 text-gray-600 hover:text-blue-700 shadow-sm hover:shadow-md'
      }`}
    >
      {text}
      <div className={`absolute -inset-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity blur-xl pointer-events-none ${isDark ? 'bg-blue-500/10' : 'bg-blue-400/10'}`} />
    </motion.button>
  );
};

/* ═══════════════════════════════════════════════════════
   3D TILT CARD
   ═══════════════════════════════════════════════════════ */
const TiltCard = ({ children, className, isDark }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), { stiffness: 300, damping: 30 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), { stiffness: 300, damping: 30 });

  const handleMouse = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => { x.set(0); y.set(0); };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={handleLeave}
      style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════
   FAQ ACCORDION ITEM
   ═══════════════════════════════════════════════════════ */
const FAQItem = ({ question, answer, isDark, accentColor }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      className={`border rounded-2xl overflow-hidden transition-colors ${isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-gray-200 bg-white/50'} backdrop-blur-sm`}
      whileHover={{ scale: 1.005 }}
    >
      <button onClick={() => setOpen(!open)} className={`w-full flex items-center justify-between p-6 text-left ${isDark ? 'text-white' : 'text-gray-900'}`}>
        <span className="font-semibold text-base pr-4" style={{ fontFamily: 'Outfit' }}>{question}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }}>
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: accentColor }} />
        </motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className={`px-6 pb-6 text-sm leading-relaxed ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════
   TYPEWRITER HOOK
   ═══════════════════════════════════════════════════════ */
const useTypewriter = (phrases, typingSpeed = 60, deletingSpeed = 30, pause = 2000) => {
  const [text, setText] = useState('');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = phrases[phraseIndex] || '';
    let timeout;

    if (!isDeleting && text === current) {
      timeout = setTimeout(() => setIsDeleting(true), pause);
    } else if (isDeleting && text === '') {
      setIsDeleting(false);
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
    } else {
      timeout = setTimeout(() => {
        setText(current.substring(0, text.length + (isDeleting ? -1 : 1)));
      }, isDeleting ? deletingSpeed : typingSpeed);
    }
    return () => clearTimeout(timeout);
  }, [text, isDeleting, phraseIndex, phrases, typingSpeed, deletingSpeed, pause]);

  return text;
};


/* ═══════════════════════════════════════════════════════
   MAIN LANDING PAGE
   ═══════════════════════════════════════════════════════ */
export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme !== 'aurora';
  const isCyber = theme === 'cyber';
  const isEmber = theme === 'ember';
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const searchRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 50, damping: 30 });
  const springY = useSpring(mouseY, { stiffness: 50, damping: 30 });

  // Section refs for scroll animations
  const howRef = useRef(null);
  const platformsRef = useRef(null);
  const whyRef = useRef(null);
  const testimonialsRef = useRef(null);
  const pricingRef = useRef(null);
  const faqRef = useRef(null);
  const ctaRef = useRef(null);

  const howInView = useInView(howRef, { once: true, margin: '-80px' });
  const platformsInView = useInView(platformsRef, { once: true, margin: '-80px' });
  const whyInView = useInView(whyRef, { once: true, margin: '-80px' });
  const testimonialsInView = useInView(testimonialsRef, { once: true, margin: '-80px' });
  const pricingInView = useInView(pricingRef, { once: true, margin: '-80px' });
  const faqInView = useInView(faqRef, { once: true, margin: '-80px' });
  const ctaInView = useInView(ctaRef, { once: true, margin: '-80px' });

  // Parallax scroll
  const { scrollYProgress } = useScroll();
  const heroParallax = useTransform(scrollYProgress, [0, 0.3], [0, -120]);

  useEffect(() => {
    suggestionsAPI.list().then(res => setSuggestions(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      mouseX.set(x);
      mouseY.set(y);
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, [mouseX, mouseY]);

  const lang = i18n.language;
  const getSuggestionText = (s) => {
    if (lang === 'ar' && s.text_ar) return s.text_ar;
    if (lang === 'de' && s.text_de) return s.text_de;
    return s.text;
  };

  const tags = suggestions.map(getSuggestionText);
  const filteredTags = searchValue
    ? tags.filter(tag => tag.toLowerCase().includes(searchValue.toLowerCase()))
    : tags;

  const handleTagClick = (tag) => {
    setSearchValue(tag);
    navigate('/submit', { state: { issueType: tag } });
  };

  const handleSearch = () => {
    if (searchValue.trim()) {
      navigate('/submit', { state: { issueType: searchValue } });
    }
  };

  const accentColor = isCyber ? '#00FF88' : isEmber ? '#F97316' : '#3B82F6';
  const accentGlow = isCyber ? 'rgba(0,255,136,' : isEmber ? 'rgba(249,115,22,' : 'rgba(59,130,246,';
  const gradientFrom = isCyber ? '#00FF88' : isEmber ? '#F97316' : '#3B82F6';
  const gradientTo = isCyber ? '#A855F7' : isEmber ? '#FB7185' : '#6366F1';

  // Typewriter placeholders
  const placeholders = [
    lang === 'ar' ? 'تم حظر حسابي على فيسبوك...' : lang === 'de' ? 'Mein Facebook-Konto wurde gesperrt...' : 'My Facebook account was banned...',
    lang === 'ar' ? 'يوتيوب حذف فيديو الخاص بي...' : lang === 'de' ? 'YouTube hat mein Video entfernt...' : 'YouTube removed my video...',
    lang === 'ar' ? 'انستغرام قيّد حسابي...' : lang === 'de' ? 'Instagram hat mein Konto eingeschränkt...' : 'Instagram shadowbanned me...',
    lang === 'ar' ? 'تيك توك أوقف حسابي...' : lang === 'de' ? 'TikTok hat mein Konto suspendiert...' : 'TikTok suspended my account...',
    lang === 'ar' ? 'أمازون رفض إرجاع منتجي...' : lang === 'de' ? 'Amazon hat meine Rücksendung abgelehnt...' : 'Amazon denied my product return...',
  ];
  const typedPlaceholder = useTypewriter(placeholders, 50, 25, 2500);

  const stats = [
    { value: '10000', suffix: '+', label: t('tags.banned') ? (lang === 'ar' ? 'طلب مقدّم' : lang === 'de' ? 'Eingereichte Beschwerden' : 'Appeals Filed') : 'Appeals Filed' },
    { value: '95', suffix: '%', label: lang === 'ar' ? 'نسبة النجاح' : lang === 'de' ? 'Erfolgsrate' : 'Success Rate' },
    { value: '50', suffix: '+', label: lang === 'ar' ? 'دولة' : lang === 'de' ? 'Länder' : 'Countries' },
    { value: '27', suffix: '', label: lang === 'ar' ? 'دولة أوروبية' : lang === 'de' ? 'EU-Länder' : 'EU Certified' },
  ];

  const steps = [
    { icon: Search, num: '01', title: lang === 'ar' ? 'اختر المنصة' : lang === 'de' ? 'Plattform wählen' : 'Choose Platform', desc: lang === 'ar' ? 'حدد المنصة التي تريد تقديم شكوى ضدها من قائمتنا الشاملة.' : lang === 'de' ? 'Wählen Sie die Plattform aus unserer umfassenden Liste.' : 'Select the platform you want to file against from our comprehensive list.' },
    { icon: FileText, num: '02', title: lang === 'ar' ? 'صف المشكلة' : lang === 'de' ? 'Problem beschreiben' : 'Describe Your Issue', desc: lang === 'ar' ? 'أخبرنا بما حدث. نحن نساعدك في صياغة الطلب القانوني.' : lang === 'de' ? 'Erzählen Sie uns, was passiert ist. Wir helfen bei der rechtlichen Formulierung.' : 'Tell us what happened. We help you craft the legal appeal document.' },
    { icon: Scale, num: '03', title: lang === 'ar' ? 'نحن نتولى الباقي' : lang === 'de' ? 'Wir erledigen den Rest' : 'We Handle The Rest', desc: lang === 'ar' ? 'فريقنا القانوني يتولى تقديم الطلب ومتابعته حتى الحصول على نتيجة.' : lang === 'de' ? 'Unser Rechtsteam reicht die Beschwerde ein und verfolgt sie bis zum Ergebnis.' : 'Our legal team files the appeal and follows up until you get a resolution.' },
  ];

  const testimonials = [
    { name: 'Sarah M.', location: lang === 'ar' ? 'ألمانيا' : 'Germany', initials: 'SM', stars: 5, text: lang === 'ar' ? 'استعدت حسابي على انستغرام بعد 3 أيام فقط! خدمة لا تصدق.' : lang === 'de' ? 'Mein Instagram-Konto wurde in nur 3 Tagen wiederhergestellt!' : 'Got my Instagram account back in just 3 days! Incredible service.', gradient: 'from-blue-500 to-purple-500' },
    { name: 'Marco R.', location: lang === 'ar' ? 'إيطاليا' : 'Italy', initials: 'MR', stars: 5, text: lang === 'ar' ? 'يوتيوب حذف قناتي بالكامل. myAct ساعدني في استعادتها مع كل المحتوى.' : lang === 'de' ? 'YouTube hat meinen gesamten Kanal gelöscht. myAct hat mir geholfen, alles wiederherzustellen.' : 'YouTube deleted my entire channel. myAct helped me get it all back with every video restored.', gradient: 'from-purple-500 to-pink-500' },
    { name: 'Anna K.', location: lang === 'ar' ? 'فرنسا' : 'France', initials: 'AK', stars: 5, text: lang === 'ar' ? 'أمازون رفض إرجاع منتجي. بفضل myAct حصلت على حقي كاملاً.' : lang === 'de' ? 'Amazon hat meine Rückgabe abgelehnt. Dank myAct habe ich mein volles Recht bekommen.' : 'Amazon refused my return. Thanks to myAct, I got my full refund and an apology.', gradient: 'from-pink-500 to-orange-500' },
  ];

  const plans = [
    {
      name: lang === 'ar' ? 'أساسي' : lang === 'de' ? 'Basis' : 'Basic',
      price: '29',
      period: lang === 'ar' ? 'لكل طلب' : lang === 'de' ? 'pro Beschwerde' : 'per appeal',
      features: [
        lang === 'ar' ? 'طلب واحد' : lang === 'de' ? 'Eine Beschwerde' : 'Single appeal filing',
        lang === 'ar' ? 'مراجعة قانونية' : lang === 'de' ? 'Rechtliche Prüfung' : 'Legal review',
        lang === 'ar' ? 'دعم بالبريد' : lang === 'de' ? 'E-Mail-Support' : 'Email support',
        lang === 'ar' ? 'تتبع الحالة' : lang === 'de' ? 'Statusverfolgung' : 'Status tracking',
      ],
      popular: false,
    },
    {
      name: lang === 'ar' ? 'احترافي' : lang === 'de' ? 'Professionell' : 'Professional',
      price: '79',
      period: lang === 'ar' ? 'لكل طلب' : lang === 'de' ? 'pro Beschwerde' : 'per appeal',
      features: [
        lang === 'ar' ? 'طلب مع تسريع' : lang === 'de' ? 'Beschleunigte Beschwerde' : 'Expedited appeal filing',
        lang === 'ar' ? 'مراجعة قانونية متقدمة' : lang === 'de' ? 'Erweiterte rechtliche Prüfung' : 'Advanced legal review',
        lang === 'ar' ? 'دعم مباشر' : lang === 'de' ? 'Direkter Support' : 'Priority support',
        lang === 'ar' ? 'تقرير مفصل' : lang === 'de' ? 'Detaillierter Bericht' : 'Detailed report',
        lang === 'ar' ? 'متابعة مستمرة' : lang === 'de' ? 'Fortlaufende Nachverfolgung' : 'Continuous follow-up',
      ],
      popular: true,
    },
    {
      name: lang === 'ar' ? 'مؤسسات' : lang === 'de' ? 'Unternehmen' : 'Enterprise',
      price: '199',
      period: lang === 'ar' ? '/شهر' : lang === 'de' ? '/Monat' : '/month',
      features: [
        lang === 'ar' ? 'طلبات غير محدودة' : lang === 'de' ? 'Unbegrenzte Beschwerden' : 'Unlimited appeals',
        lang === 'ar' ? 'فريق قانوني مخصص' : lang === 'de' ? 'Dediziertes Rechtsteam' : 'Dedicated legal team',
        lang === 'ar' ? 'دعم 24/7' : lang === 'de' ? '24/7-Support' : '24/7 support',
        lang === 'ar' ? 'تقارير شهرية' : lang === 'de' ? 'Monatliche Berichte' : 'Monthly reports',
        lang === 'ar' ? 'وصول API' : lang === 'de' ? 'API-Zugang' : 'API access',
        lang === 'ar' ? 'مدير حساب' : lang === 'de' ? 'Kontomanager' : 'Account manager',
      ],
      popular: false,
    },
  ];

  const faqs = [
    {
      q: lang === 'ar' ? 'ما هو قانون الخدمات الرقمية (DSA)؟' : lang === 'de' ? 'Was ist der Digital Services Act (DSA)?' : 'What is the Digital Services Act (DSA)?',
      a: lang === 'ar' ? 'قانون الخدمات الرقمية هو تشريع أوروبي يلزم شركات التكنولوجيا الكبرى بالشفافية والعدالة في قرارات إدارة المحتوى، ويمنح المستخدمين حق الطعن في القرارات غير العادلة.' : lang === 'de' ? 'Der DSA ist eine EU-Verordnung, die große Technologieunternehmen zu Transparenz und Fairness bei der Inhaltsmoderation verpflichtet und Nutzern das Recht gibt, unfaire Entscheidungen anzufechten.' : 'The Digital Services Act is an EU regulation that requires large tech companies to be transparent and fair in content moderation decisions, giving users the right to appeal unfair decisions.',
    },
    {
      q: lang === 'ar' ? 'هل أحتاج إلى محامٍ؟' : lang === 'de' ? 'Brauche ich einen Anwalt?' : 'Do I need a lawyer?',
      a: lang === 'ar' ? 'لا! myAct يتولى كل الجوانب القانونية نيابة عنك. فريقنا المتخصص يصيغ الطلب ويتابعه حتى الحصول على نتيجة.' : lang === 'de' ? 'Nein! myAct übernimmt alle rechtlichen Aspekte für Sie. Unser Expertenteam formuliert die Beschwerde und verfolgt sie bis zum Ergebnis.' : 'No! myAct handles all the legal aspects for you. Our expert team drafts the appeal and follows up until you get a resolution.',
    },
    {
      q: lang === 'ar' ? 'كم يستغرق الأمر؟' : lang === 'de' ? 'Wie lange dauert es?' : 'How long does the process take?',
      a: lang === 'ar' ? 'تقديم الطلب يستغرق حوالي 5 دقائق. الاستجابة من المنصة عادة خلال 48 ساعة إلى أسبوعين حسب الحالة.' : lang === 'de' ? 'Das Einreichen dauert etwa 5 Minuten. Die Antwort der Plattform kommt in der Regel innerhalb von 48 Stunden bis 2 Wochen.' : 'Filing takes about 5 minutes. Platform responses typically come within 48 hours to 2 weeks depending on the case.',
    },
    {
      q: lang === 'ar' ? 'هل الخدمة متاحة في بلدي؟' : lang === 'de' ? 'Ist der Service in meinem Land verfügbar?' : 'Is the service available in my country?',
      a: lang === 'ar' ? 'نعم! إذا كنت في الاتحاد الأوروبي أو المنطقة الاقتصادية الأوروبية، يحق لك تقديم طلب DSA. كما نساعد المستخدمين من خارج أوروبا الذين يتأثرون بقرارات المنصات في السوق الأوروبية.' : lang === 'de' ? 'Ja! Wenn Sie in der EU oder im EWR sind, haben Sie das Recht, eine DSA-Beschwerde einzureichen. Wir helfen auch Nutzern außerhalb Europas.' : 'Yes! If you are in the EU or EEA, you have the right to file a DSA appeal. We also help users outside Europe who are affected by platform decisions in the European market.',
    },
    {
      q: lang === 'ar' ? 'ماذا لو تم رفض طلبي؟' : lang === 'de' ? 'Was passiert, wenn meine Beschwerde abgelehnt wird?' : 'What if my appeal is rejected?',
      a: lang === 'ar' ? 'في حالة الرفض، نراجع الحالة ونقدم خيارات بديلة بما في ذلك التصعيد إلى الجهات التنظيمية الوطنية أو المحاكم المختصة.' : lang === 'de' ? 'Im Falle einer Ablehnung prüfen wir den Fall und bieten alternative Optionen, einschließlich Eskalation an nationale Regulierungsbehörden.' : 'If rejected, we review the case and provide alternative options including escalation to national regulators or competent courts.',
    },
  ];

  return (
    <div className={`relative overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#030306]' : 'bg-gray-50'}`}>

      {/* =====================================================
          SECTION 1: HERO
          ===================================================== */}
      <section className="relative min-h-screen overflow-hidden" style={{ perspective: '1200px' }}>

        {/* Animated gradient mesh background */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ x: [0, 60, 0, -40, 0], y: [0, -40, 20, 0, 0], scale: [1, 1.1, 1, 0.95, 1] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-1/4 -left-1/4 w-[800px] h-[800px] rounded-full pointer-events-none"
            style={{ background: isDark ? 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)' }}
          />
          <motion.div
            animate={{ x: [0, -50, 30, 0], y: [0, 30, -20, 0], scale: [1, 0.9, 1.1, 1] }}
            transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-1/4 -right-1/4 w-[700px] h-[700px] rounded-full pointer-events-none"
            style={{ background: isDark ? 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 70%)' }}
          />
          <motion.div
            animate={{ x: [0, 40, -30, 0], y: [0, -50, 30, 0] }}
            transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-1/2 left-1/3 w-[600px] h-[600px] rounded-full pointer-events-none"
            style={{ background: isDark ? 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 70%)' }}
          />
          <motion.div
            animate={{ x: [0, -30, 50, 0], y: [0, 40, -30, 0], scale: [1, 1.15, 0.9, 1] }}
            transition={{ duration: 28, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
            style={{ background: isDark ? 'radial-gradient(circle, rgba(236,72,153,0.08) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(236,72,153,0.04) 0%, transparent 70%)' }}
          />
          <motion.div
            animate={{ x: [0, 25, -40, 0], y: [0, -35, 25, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-1/4 right-1/2 w-[400px] h-[400px] rounded-full pointer-events-none"
            style={{ background: isDark ? 'radial-gradient(circle, rgba(6,182,212,0.08) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(6,182,212,0.04) 0%, transparent 70%)' }}
          />
        </div>

        {/* Floating 3D tech company icons */}
        <motion.div className="absolute inset-0 overflow-hidden" style={{ x: springX, y: springY, transformStyle: 'preserve-3d', y: heroParallax }}>
          {TECH_COMPANIES.map((p, i) => (
            <Logo3D key={p.name} Icon={p.icon} color={p.color} index={i} total={TECH_COMPANIES.length} isDark={isDark} />
          ))}
        </motion.div>

        {/* Perspective grid */}
        <div className="absolute inset-0 pointer-events-none" style={{
          opacity: isDark ? 0.02 : 0.012,
          backgroundImage: `linear-gradient(to right, ${isDark ? accentColor : '#000'} 1px, transparent 1px), linear-gradient(to bottom, ${isDark ? accentColor : '#000'} 1px, transparent 1px)`,
          backgroundSize: '80px 80px',
          transform: 'perspective(500px) rotateX(30deg)', transformOrigin: 'center 120%',
          maskImage: 'linear-gradient(to bottom, transparent 0%, black 30%, black 70%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 30%, black 70%, transparent 100%)',
        }} />

        {/* Hero Content */}
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 sm:px-6">

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-12 md:mb-14 max-w-5xl"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.7, type: 'spring' }}
              className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full text-sm mb-8 md:mb-10 border backdrop-blur-xl ${
                isDark ? 'bg-white/[0.04] border-white/[0.08] text-white/60' : 'bg-blue-50/80 border-blue-200 text-blue-600'
              }`}
            >
              <Sparkles className="w-4 h-4" style={{ color: accentColor }} />
              <span className="font-medium">EU Digital Services Act</span>
              <Shield className="w-4 h-4" style={{ color: accentColor }} />
            </motion.div>

            {/* HUGE Title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className={`text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-extrabold tracking-[-0.04em] mb-6 md:mb-7 leading-[0.9] ${isDark ? 'text-white' : 'text-gray-900'}`}
              style={{ fontFamily: 'Outfit' }}
            >
              {t('hero.title')}{' '}
              <span className="relative inline-block">
                <span className="bg-clip-text text-transparent" style={{
                  backgroundImage: `linear-gradient(135deg, ${gradientFrom} 0%, ${gradientTo} 50%, ${isCyber ? '#00D4FF' : isEmber ? '#FBBF24' : '#06B6D4'} 100%)`
                }}>
                  {t('hero.titleHighlight')}
                </span>
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                  style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }}
                  animate={{ opacity: [0.3, 0.8, 0.3], scaleX: [0.8, 1, 0.8] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
              </span>
            </motion.h1>

            {/* Subtitle with fade-in */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className={`text-lg md:text-xl max-w-2xl mx-auto leading-relaxed ${isDark ? 'text-white/45' : 'text-gray-500'}`}
            >
              {t('hero.subtitle')}
            </motion.p>
          </motion.div>

          {/* EPIC SEARCH BAR */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.7, duration: 0.8, type: 'spring', stiffness: 100 }}
            className="w-full max-w-2xl mb-8 relative"
          >
            {/* Outer glow ring */}
            <motion.div
              className="absolute -inset-1 rounded-[28px] pointer-events-none"
              animate={searchFocused ? { opacity: 1, scale: 1 } : { opacity: 0.4, scale: 0.98 }}
              transition={{ duration: 0.5 }}
              style={{ background: `linear-gradient(135deg, ${accentGlow}0.3), transparent, ${accentGlow}0.2))`, filter: `blur(${searchFocused ? '20px' : '12px'})` }}
            />

            <motion.div
              layout
              className={`relative rounded-3xl overflow-hidden transition-all duration-500 ${
                isDark ? 'bg-[#0A0A0C]/80' : 'bg-white/90'
              } border backdrop-blur-2xl ${searchFocused ? 'shadow-2xl' : 'shadow-lg'} ${
                searchFocused ? isDark ? 'border-blue-500/30' : 'border-blue-400/50' : isDark ? 'border-white/[0.08]' : 'border-gray-200'
              }`}
              style={{
                boxShadow: searchFocused
                  ? `0 0 80px -15px ${accentGlow}0.4), 0 0 40px -10px ${accentGlow}0.2), 0 20px 60px -20px rgba(0,0,0,0.3)`
                  : isDark ? `0 0 40px -20px ${accentGlow}0.2), 0 10px 40px -15px rgba(0,0,0,0.3)` : '0 10px 40px -15px rgba(0,0,0,0.08)',
              }}
            >
              {/* Input Row */}
              <div className="flex items-center h-16 md:h-[72px] px-6">
                <motion.div animate={{ rotate: searchFocused ? 90 : 0 }} transition={{ duration: 0.3 }}>
                  <Search className={`w-5 h-5 transition-colors flex-shrink-0 ${searchFocused ? '' : isDark ? 'text-white/25' : 'text-gray-300'}`}
                    style={searchFocused ? { color: accentColor } : {}} />
                </motion.div>
                <input
                  ref={searchRef}
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 300)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={searchValue ? '' : typedPlaceholder}
                  className={`flex-1 bg-transparent border-none outline-none text-lg mx-4 ${isDark ? 'text-white placeholder-white/20' : 'text-gray-900 placeholder-gray-300'}`}
                />
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    onClick={handleSearch}
                    className="h-11 px-7 text-white rounded-2xl text-sm font-semibold flex-shrink-0 border-0 relative overflow-hidden group"
                    style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      {t('hero.cta')}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                    {/* Shine effect */}
                    <motion.div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.2) 45%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.2) 55%, transparent 60%)' }}
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    />
                  </Button>
                </motion.div>
              </div>

              {/* Cloud Suggestions Inside */}
              <AnimatePresence>
                {searchFocused && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className={`overflow-hidden border-t ${isDark ? 'border-white/[0.05]' : 'border-gray-100'}`}
                  >
                    <div className="p-6 pb-7">
                      <div className="flex items-center gap-2 mb-5">
                        <Sparkles className="w-3.5 h-3.5" style={{ color: accentColor }} />
                        <p className={`text-xs uppercase tracking-widest font-semibold ${isDark ? 'text-white/25' : 'text-gray-400'}`}>
                          {lang === 'ar' ? 'مشاكل شائعة' : lang === 'de' ? 'Beliebte Probleme' : 'Popular Issues'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-3 justify-center min-h-[120px] items-center py-2">
                        <AnimatePresence mode="popLayout">
                          {filteredTags.slice(0, 12).map((tag, i) => (
                            <CloudTag key={tag} text={tag} index={i} onClick={() => handleTagClick(tag)} isDark={isDark} icon={ISSUE_ICONS[i % ISSUE_ICONS.length]} />
                          ))}
                        </AnimatePresence>
                      </div>
                      {filteredTags.length === 0 && (
                        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`text-center py-6 text-sm ${isDark ? 'text-white/15' : 'text-gray-300'}`}>
                          {lang === 'ar' ? 'لا توجد نتائج مطابقة' : lang === 'de' ? 'Keine Vorschläge gefunden' : 'No matching suggestions'}
                        </motion.p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>

          {/* Floating Tags Outside */}
          <AnimatePresence>
            {!searchFocused && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, y: 15 }}
                transition={{ delay: 0.9 }}
                className="flex flex-wrap justify-center gap-3 max-w-3xl mb-10"
              >
                {tags.slice(0, 8).map((tag, i) => (
                  <FloatingCloudTag key={tag} text={tag} index={i} onClick={() => handleTagClick(tag)} isDark={isDark} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className={`flex flex-wrap justify-center gap-4 md:gap-6 text-sm mb-8 ${isDark ? 'text-white/30' : 'text-gray-400'}`}
          >
            {[
              { icon: Shield, text: lang === 'ar' ? 'لا حاجة لمحامٍ' : lang === 'de' ? 'Kein Anwalt nötig' : 'No lawyers needed' },
              { icon: Clock, text: lang === 'ar' ? '5 دقائق فقط' : lang === 'de' ? 'Nur 5 Minuten' : '5 minutes' },
              { icon: Globe, text: lang === 'ar' ? 'حقوق أوروبية' : lang === 'de' ? 'EU-gestützte Rechte' : 'EU-backed rights' },
            ].map((badge, i) => (
              <motion.div
                key={badge.text}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 + i * 0.1 }}
                className="flex items-center gap-2"
              >
                <badge.icon className="w-4 h-4" style={{ color: accentColor, opacity: 0.6 }} />
                <span>{badge.text}</span>
                {i < 2 && <span className="ml-2 hidden sm:inline">·</span>}
              </motion.div>
            ))}
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2.5, repeat: Infinity }}
            className="absolute bottom-8 cursor-pointer"
            onClick={() => howRef.current?.scrollIntoView({ behavior: 'smooth' })}
          >
            <div className={`flex flex-col items-center gap-2 ${isDark ? 'text-white/15' : 'text-gray-300'}`}>
              <span className="text-xs uppercase tracking-widest font-medium">{lang === 'ar' ? 'اكتشف المزيد' : lang === 'de' ? 'Mehr erfahren' : 'Scroll to explore'}</span>
              <ChevronDown className="w-5 h-5" />
            </div>
          </motion.div>
        </div>
      </section>


      {/* =====================================================
          SECTION 2: HOW IT WORKS
          ===================================================== */}
      <section ref={howRef} className="relative py-24 md:py-32 px-4 sm:px-6 overflow-hidden">
        {/* Background accent */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: isDark
            ? `radial-gradient(ellipse 80% 50% at 50% 50%, ${accentGlow}0.04) 0%, transparent 100%)`
            : 'radial-gradient(ellipse 80% 50% at 50% 50%, rgba(59,130,246,0.03) 0%, transparent 100%)',
        }} />

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={howInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 md:mb-20"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={howInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.1, type: 'spring' }}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold mb-6 border backdrop-blur-sm ${
                isDark ? 'bg-white/[0.03] border-white/[0.06] text-white/40' : 'bg-blue-50 border-blue-100 text-blue-500'
              }`}
            >
              <Zap className="w-3.5 h-3.5" style={{ color: accentColor }} />
              {lang === 'ar' ? 'كيف يعمل' : lang === 'de' ? 'So funktioniert es' : 'How It Works'}
            </motion.span>
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
              {lang === 'ar' ? 'ثلاث خطوات بسيطة' : lang === 'de' ? 'Drei einfache Schritte' : 'Three Simple Steps'}
            </h2>
            <p className={`text-lg max-w-xl mx-auto ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
              {lang === 'ar' ? 'من تقديم الطلب إلى الحصول على نتيجة، نحن معك في كل خطوة.' : lang === 'de' ? 'Von der Einreichung bis zum Ergebnis begleiten wir Sie bei jedem Schritt.' : 'From filing to resolution, we are with you every step of the way.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 40 }}
                animate={howInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 + i * 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <TiltCard isDark={isDark} className="h-full">
                  <div className={`relative h-full p-8 md:p-10 rounded-3xl border transition-all group cursor-default overflow-hidden ${
                    isDark
                      ? 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.04]'
                      : 'bg-white/70 border-gray-200 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5'
                  } backdrop-blur-xl`}>
                    {/* Step number watermark */}
                    <div className={`absolute -top-4 -right-2 text-[8rem] font-extrabold leading-none pointer-events-none ${isDark ? 'text-white/[0.02]' : 'text-gray-900/[0.03]'}`} style={{ fontFamily: 'Outfit' }}>
                      {step.num}
                    </div>

                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all group-hover:scale-110 relative ${isDark ? 'bg-white/[0.05]' : 'bg-blue-50'}`}>
                      <step.icon className="w-7 h-7" style={{ color: accentColor }} />
                      <motion.div
                        className="absolute inset-0 rounded-2xl"
                        style={{ background: `linear-gradient(135deg, ${accentGlow}0.2), transparent)` }}
                        animate={{ opacity: [0, 0.5, 0] }}
                        transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
                      />
                    </div>

                    <div className="mb-2">
                      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: accentColor, opacity: 0.6 }}>
                        {lang === 'ar' ? `خطوة ${step.num}` : lang === 'de' ? `Schritt ${step.num}` : `Step ${step.num}`}
                      </span>
                    </div>
                    <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
                      {step.title}
                    </h3>
                    <p className={`text-sm leading-relaxed ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                      {step.desc}
                    </p>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* =====================================================
          SECTION 3: PLATFORMS WE FIGHT
          ===================================================== */}
      <section ref={platformsRef} className="relative py-24 md:py-32 px-4 sm:px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={platformsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 md:mb-20"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={platformsInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.1, type: 'spring' }}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold mb-6 border backdrop-blur-sm ${
                isDark ? 'bg-white/[0.03] border-white/[0.06] text-white/40' : 'bg-blue-50 border-blue-100 text-blue-500'
              }`}
            >
              <Globe className="w-3.5 h-3.5" style={{ color: accentColor }} />
              {lang === 'ar' ? 'المنصات' : lang === 'de' ? 'Plattformen' : 'Platforms'}
            </motion.span>
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
              {lang === 'ar' ? 'المنصات التي نحاربها' : lang === 'de' ? 'Plattformen, die wir bekämpfen' : 'Platforms We Fight'}
            </h2>
            <p className={`text-lg max-w-xl mx-auto ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
              {lang === 'ar' ? 'قدّم طلبك ضد أي من هذه المنصات بحماية قانون الخدمات الرقمية.' : lang === 'de' ? 'Reichen Sie Ihre Beschwerde gegen jede dieser Plattformen ein.' : 'File your appeal against any of these platforms under DSA protection.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {TECH_COMPANIES.map((company, i) => (
              <motion.div
                key={company.name}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={platformsInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ delay: 0.05 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <TiltCard isDark={isDark}>
                  <motion.button
                    onClick={() => navigate(`/submit?platform=${encodeURIComponent(company.name)}`)}
                    whileHover={{ y: -4 }}
                    whileTap={{ scale: 0.97 }}
                    className={`w-full p-5 md:p-6 rounded-2xl border backdrop-blur-xl transition-all group text-center cursor-pointer ${
                      isDark
                        ? 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.15] hover:bg-white/[0.05]'
                        : 'bg-white/70 border-gray-200 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-500/5'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative">
                        <company.icon className="w-10 h-10 md:w-12 md:h-12 transition-transform group-hover:scale-110" style={{ color: company.color }} />
                        <motion.div
                          className="absolute -inset-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ background: `radial-gradient(circle, ${company.color}20 0%, transparent 70%)` }}
                        />
                      </div>
                      <span className={`text-sm font-semibold ${isDark ? 'text-white/70 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'} transition-colors`}>
                        {company.name}
                      </span>
                      <span className="text-xs flex items-center gap-1 transition-all opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0" style={{ color: accentColor }}>
                        {lang === 'ar' ? 'قدّم طلبك' : lang === 'de' ? 'Beschwerde' : 'File Appeal'} <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </motion.button>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* =====================================================
          SECTION 4: WHY MYACT (Stats)
          ===================================================== */}
      <section ref={whyRef} className="relative py-24 md:py-32 px-4 sm:px-6 overflow-hidden">
        {/* Gradient divider glow */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: isDark
            ? `radial-gradient(ellipse 60% 40% at 50% 50%, ${accentGlow}0.06) 0%, transparent 100%)`
            : 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(59,130,246,0.04) 0%, transparent 100%)',
        }} />

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={whyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 md:mb-20"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={whyInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.1, type: 'spring' }}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold mb-6 border backdrop-blur-sm ${
                isDark ? 'bg-white/[0.03] border-white/[0.06] text-white/40' : 'bg-blue-50 border-blue-100 text-blue-500'
              }`}
            >
              <Award className="w-3.5 h-3.5" style={{ color: accentColor }} />
              {lang === 'ar' ? 'لماذا myAct' : lang === 'de' ? 'Warum myAct' : 'Why myAct'}
            </motion.span>
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
              {lang === 'ar' ? 'أرقام تتحدث عن نفسها' : lang === 'de' ? 'Zahlen sprechen für sich' : 'Numbers Speak for Themselves'}
            </h2>
          </motion.div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-16 md:mb-20">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                animate={whyInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.15 * i, duration: 0.6 }}
              >
                <div className={`relative p-6 md:p-8 rounded-2xl border text-center backdrop-blur-xl overflow-hidden ${
                  isDark ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-white/70 border-gray-200'
                }`}>
                  {/* Gradient border glow */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 hover:opacity-100 transition-opacity pointer-events-none"
                    style={{ background: `linear-gradient(135deg, ${accentGlow}0.1), transparent, ${accentGlow}0.05))`, padding: '1px' }}
                  />
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} isDark={isDark} />
                  <div className={`text-sm mt-2 font-medium ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Feature cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: Scale, title: lang === 'ar' ? 'خبرة قانونية' : lang === 'de' ? 'Rechtliche Expertise' : 'Legal Expertise', desc: lang === 'ar' ? 'فريق متخصص بقوانين الاتحاد الأوروبي يتولى طلبك من البداية للنهاية.' : lang === 'de' ? 'Unser Team von EU-Rechtsexperten bearbeitet Ihre Beschwerde von Anfang bis Ende.' : 'Our team of EU law specialists handles your appeal from start to finish.' },
              { icon: Zap, title: lang === 'ar' ? 'معالجة سريعة' : lang === 'de' ? 'Schnelle Bearbeitung' : 'Fast Processing', desc: lang === 'ar' ? 'متوسط وقت الاستجابة 48 ساعة. خيار التسريع متاح.' : lang === 'de' ? 'Durchschnittliche Antwortzeit von 48 Stunden. Beschleunigungsoption verfügbar.' : 'Average response time of 48 hours. Expedite option available.' },
              { icon: Globe, title: lang === 'ar' ? 'تغطية أوروبية' : lang === 'de' ? 'Pan-Europäisch' : 'Pan-European', desc: lang === 'ar' ? 'تغطية في جميع دول الاتحاد الأوروبي الـ 27 مع دعم قانوني محلي.' : lang === 'de' ? 'Abdeckung in allen 27 EU-Mitgliedstaaten mit lokalem Rechtssupport.' : 'Coverage across all 27 EU member states with local legal support.' },
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={whyInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3 + i * 0.12, duration: 0.6 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className={`group p-7 md:p-8 backdrop-blur-xl rounded-2xl border transition-all cursor-default ${
                  isDark ? 'bg-white/[0.02] border-white/[0.04] hover:border-white/[0.12] hover:bg-white/[0.04]' : 'bg-white/70 border-gray-200 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-all group-hover:scale-110 ${isDark ? 'bg-white/[0.04]' : 'bg-blue-50'}`}>
                  <feature.icon className="w-6 h-6" style={{ color: accentColor }} />
                </div>
                <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>{feature.title}</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-white/35' : 'text-gray-500'}`}>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* =====================================================
          SECTION 5: TESTIMONIALS
          ===================================================== */}
      <section ref={testimonialsRef} className="relative py-24 md:py-32 px-4 sm:px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 md:mb-20"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={testimonialsInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.1, type: 'spring' }}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold mb-6 border backdrop-blur-sm ${
                isDark ? 'bg-white/[0.03] border-white/[0.06] text-white/40' : 'bg-blue-50 border-blue-100 text-blue-500'
              }`}
            >
              <Heart className="w-3.5 h-3.5" style={{ color: accentColor }} />
              {lang === 'ar' ? 'آراء العملاء' : lang === 'de' ? 'Kundenstimmen' : 'Testimonials'}
            </motion.span>
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
              {lang === 'ar' ? 'ماذا يقول عملاؤنا' : lang === 'de' ? 'Was unsere Kunden sagen' : 'What Our Users Say'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((t_item, i) => (
              <motion.div
                key={t_item.name}
                initial={{ opacity: 0, x: i === 0 ? -40 : i === 2 ? 40 : 0, y: 30 }}
                animate={testimonialsInView ? { opacity: 1, x: 0, y: 0 } : {}}
                transition={{ delay: 0.15 * i, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <TiltCard isDark={isDark}>
                  <div className={`h-full p-7 md:p-8 rounded-2xl border backdrop-blur-xl ${
                    isDark ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-white/70 border-gray-200'
                  }`}>
                    {/* Stars */}
                    <div className="flex gap-1 mb-5">
                      {Array.from({ length: t_item.stars }).map((_, si) => (
                        <Star key={si} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>

                    {/* Quote */}
                    <p className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                      "{t_item.text}"
                    </p>

                    {/* User */}
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold bg-gradient-to-br ${t_item.gradient}`}>
                        {t_item.initials}
                      </div>
                      <div>
                        <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t_item.name}</div>
                        <div className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{t_item.location}</div>
                      </div>
                    </div>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* =====================================================
          SECTION 6: PRICING
          ===================================================== */}
      <section ref={pricingRef} className="relative py-24 md:py-32 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: isDark
            ? `radial-gradient(ellipse 70% 50% at 50% 50%, ${accentGlow}0.04) 0%, transparent 100%)`
            : 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(59,130,246,0.03) 0%, transparent 100%)',
        }} />

        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={pricingInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 md:mb-20"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={pricingInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.1, type: 'spring' }}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold mb-6 border backdrop-blur-sm ${
                isDark ? 'bg-white/[0.03] border-white/[0.06] text-white/40' : 'bg-blue-50 border-blue-100 text-blue-500'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" style={{ color: accentColor }} />
              {t('plans.title')}
            </motion.span>
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
              {lang === 'ar' ? 'خطط تناسب الجميع' : lang === 'de' ? 'Pläne für jeden' : 'Plans for Everyone'}
            </h2>
            <p className={`text-lg max-w-xl mx-auto ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
              {lang === 'ar' ? 'اختر الخطة المناسبة لك وابدأ في الدفاع عن حقوقك.' : lang === 'de' ? 'Wählen Sie den passenden Plan und verteidigen Sie Ihre Rechte.' : 'Choose the right plan and start defending your digital rights.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 40 }}
                animate={pricingInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 + i * 0.15, duration: 0.7 }}
                className="relative"
              >
                {/* Popular badge */}
                {plan.popular && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={pricingInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.4, type: 'spring' }}
                    className="absolute -top-4 left-1/2 -translate-x-1/2 z-10"
                  >
                    <span className="px-4 py-1.5 rounded-full text-xs font-bold text-white" style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}>
                      {t('plans.popular')}
                    </span>
                  </motion.div>
                )}

                <TiltCard isDark={isDark} className="h-full">
                  <div className={`relative h-full p-7 md:p-8 rounded-2xl border backdrop-blur-xl transition-all overflow-hidden ${
                    plan.popular
                      ? isDark
                        ? 'bg-white/[0.04] border-white/[0.12]'
                        : 'bg-white border-blue-300 shadow-xl shadow-blue-500/10'
                      : isDark
                        ? 'bg-white/[0.02] border-white/[0.06]'
                        : 'bg-white/70 border-gray-200'
                  }`}>
                    {/* Gradient border for popular */}
                    {plan.popular && (
                      <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
                        padding: '1px',
                        background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
                        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        maskComposite: 'xor',
                        WebkitMaskComposite: 'xor',
                      }} />
                    )}

                    <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
                      {plan.name}
                    </h3>

                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-xs" style={{ color: accentColor }}>€</span>
                      <span className={`text-5xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
                        {plan.price}
                      </span>
                      <span className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{plan.period}</span>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3">
                          <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accentColor }} />
                          <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        onClick={() => navigate('/submit')}
                        className={`w-full h-12 rounded-xl font-semibold text-sm border-0 relative overflow-hidden group ${
                          plan.popular ? 'text-white' : isDark ? 'text-white' : 'text-gray-900'
                        }`}
                        style={{
                          background: plan.popular
                            ? `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`
                            : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                        }}
                      >
                        <span className="relative z-10">{t('plans.subscribe')}</span>
                        {plan.popular && (
                          <motion.div
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.2) 45%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.2) 55%, transparent 60%)' }}
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                          />
                        )}
                      </Button>
                    </motion.div>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* =====================================================
          SECTION 7: FAQ
          ===================================================== */}
      <section ref={faqRef} className="relative py-24 md:py-32 px-4 sm:px-6 overflow-hidden">
        <div className="max-w-3xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={faqInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16 md:mb-20"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={faqInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.1, type: 'spring' }}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold mb-6 border backdrop-blur-sm ${
                isDark ? 'bg-white/[0.03] border-white/[0.06] text-white/40' : 'bg-blue-50 border-blue-100 text-blue-500'
              }`}
            >
              <MessageSquareOff className="w-3.5 h-3.5" style={{ color: accentColor }} />
              FAQ
            </motion.span>
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
              {lang === 'ar' ? 'أسئلة شائعة' : lang === 'de' ? 'Häufige Fragen' : 'Frequently Asked Questions'}
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={faqInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.08 * i, duration: 0.5 }}
              >
                <FAQItem question={faq.q} answer={faq.a} isDark={isDark} accentColor={accentColor} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* =====================================================
          SECTION 8: FINAL CTA
          ===================================================== */}
      <section ref={ctaRef} className="relative py-24 md:py-32 px-4 sm:px-6 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={ctaInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto relative"
        >
          <div className={`relative p-10 md:p-16 rounded-3xl border backdrop-blur-2xl overflow-hidden text-center ${
            isDark ? 'bg-white/[0.03] border-white/[0.08]' : 'bg-white/80 border-gray-200'
          }`}>
            {/* Background gradient */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: `linear-gradient(135deg, ${accentGlow}0.08) 0%, transparent 50%, ${accentGlow}0.04) 100%)`,
            }} />
            <motion.div
              animate={{ x: [0, 30, 0, -30, 0], y: [0, -20, 0, 20, 0] }}
              transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full pointer-events-none"
              style={{ background: `radial-gradient(circle, ${accentGlow}0.1) 0%, transparent 70%)` }}
            />

            <div className="relative z-10">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={ctaInView ? { scale: 1, opacity: 1 } : {}}
                transition={{ delay: 0.2, type: 'spring' }}
                className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-8 ${isDark ? 'bg-white/[0.06]' : 'bg-blue-50'}`}
              >
                <Shield className="w-8 h-8" style={{ color: accentColor }} />
              </motion.div>

              <h2 className={`text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] mb-5 ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Outfit' }}>
                {lang === 'ar' ? 'مستعد للمواجهة؟' : lang === 'de' ? 'Bereit sich zu wehren?' : 'Ready to Fight Back?'}
              </h2>

              <p className={`text-lg max-w-lg mx-auto mb-10 ${isDark ? 'text-white/45' : 'text-gray-500'}`}>
                {lang === 'ar' ? 'لا تدع شركات التكنولوجيا الكبرى تسلبك حقوقك الرقمية. ابدأ الآن.' : lang === 'de' ? 'Lassen Sie sich Ihre digitalen Rechte nicht von Big Tech nehmen. Starten Sie jetzt.' : "Don't let Big Tech take away your digital rights. Start your appeal today."}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    onClick={() => navigate('/submit')}
                    className="h-14 px-10 text-white rounded-2xl text-base font-bold border-0 relative overflow-hidden group"
                    style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      {t('hero.cta')}
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                    <motion.div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.2) 45%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.2) 55%, transparent 60%)' }}
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    />
                  </Button>
                </motion.div>

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    onClick={() => navigate('/track')}
                    variant="ghost"
                    className={`h-14 px-8 rounded-2xl text-base font-semibold ${isDark ? 'text-white/60 hover:text-white hover:bg-white/[0.06]' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    {t('nav.track')}
                  </Button>
                </motion.div>
              </div>

              {/* Trust badges */}
              <div className={`flex flex-wrap justify-center gap-6 mt-10 text-xs ${isDark ? 'text-white/25' : 'text-gray-400'}`}>
                <div className="flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" />
                  <span>{lang === 'ar' ? 'بيانات آمنة' : lang === 'de' ? 'Sichere Daten' : 'Secure & Encrypted'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" />
                  <span>{lang === 'ar' ? 'متوافق مع GDPR' : lang === 'de' ? 'DSGVO-konform' : 'GDPR Compliant'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5" />
                  <span>{lang === 'ar' ? 'معتمد أوروبياً' : lang === 'de' ? 'EU-Zertifiziert' : 'EU Certified'}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

    </div>
  );
}
